import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useCertificate } from '../contexts/CertificateContext';
import { 
  Shield, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Award,
  Calendar,
  User,
  BookOpen,
  Star,
  ExternalLink,
  Copy,
  Check
} from 'lucide-react';
import toast from 'react-hot-toast';

const VerifyCertificate = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyCertificate, loading } = useCertificate();
  
  const [verificationCode, setVerificationCode] = useState(searchParams.get('code') || '');
  const [verificationResult, setVerificationResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Auto-vérification quand le code est passé dans l'URL (scan QR)
  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      verifyCertificate(code.trim()).then(result => {
        setVerificationResult(result);
      }).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!verificationCode.trim()) {
      toast.error('Veuillez entrer un code de vérification');
      return;
    }

    try {
      const result = await verifyCertificate(verificationCode.trim());
      setVerificationResult(result);
      
      if (result.success) {
        toast.success('Certificat vérifié avec succès !');
      } else {
        toast.error(result.error || 'Erreur lors de la vérification');
      }
    } catch (error) {
      toast.error('Erreur lors de la vérification');
    }
  };

  const copyVerificationCode = async () => {
    try {
      await navigator.clipboard.writeText(verificationCode);
      setCopied(true);
      toast.success('Code copié !');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Erreur lors de la copie');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCertificateTypeColor = (type) => {
    switch (type) {
      case 'course_completion':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'professional':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'skill_assessment':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'achievement':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCertificateTypeText = (type) => {
    switch (type) {
      case 'course_completion':
        return 'Certificat de Completion de Cours';
      case 'professional':
        return 'Certification Professionnelle';
      case 'skill_assessment':
        return 'Certificat d\'Évaluation de Compétences';
      case 'achievement':
        return 'Certificat de Récompense';
      default:
        return 'Certificat';
    }
  };

  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Vérification de Certificat
          </h1>
          <p className="text-gray-600">
            Vérifiez l'authenticité d'un certificat en entrant son code de vérification
          </p>
        </div>

        {/* Formulaire de vérification */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <form onSubmit={handleVerify} className="space-y-6">
            <div>
              <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 mb-2">
                Code de vérification
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  id="verificationCode"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="Entrez le code de vérification (ex: INN360-WEB-2024-001)"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono"
                />
                <button
                  type="submit"
                  disabled={loading || !verificationCode.trim()}
                  className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Vérification...' : 'Vérifier'}
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Le code de vérification se trouve en bas du certificat
              </p>
            </div>
          </form>
        </div>

        {/* Résultat de la vérification */}
        {verificationResult && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            {verificationResult.success ? (
              <div>
                {/* Succès */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-green-600">Certificat Valide</h2>
                    <p className="text-gray-600">Ce certificat est authentique et vérifié</p>
                  </div>
                </div>

                {/* Détails du certificat */}
                <div className="bg-green-50 rounded-lg p-6 mb-6">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                      <Award className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {verificationResult.certificate.course_title}
                    </h3>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${getCertificateTypeColor(verificationResult.certificate.type)}`}>
                      {getCertificateTypeText(verificationResult.certificate.type)}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <User className="w-4 h-4 text-gray-600" />
                        <span className="text-gray-600">Titulaire</span>
                      </div>
                      <p className="font-semibold text-gray-900">{verificationResult.certificate.holder_name || '—'}</p>
                    </div>

                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Calendar className="w-4 h-4 text-gray-600" />
                        <span className="text-gray-600">Date d'émission</span>
                      </div>
                      <p className="font-semibold text-gray-900">{formatDate(verificationResult.certificate.issued_at)}</p>
                    </div>

                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Star className="w-4 h-4 text-gray-600" />
                        <span className="text-gray-600">Score moyen</span>
                      </div>
                      <p className="font-semibold text-gray-900">{Math.round(Number(verificationResult.certificate.score || 0))}%</p>
                    </div>

                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <BookOpen className="w-4 h-4 text-gray-600" />
                        <span className="text-gray-600">Cours</span>
                      </div>
                      <p className="font-semibold text-gray-900">{verificationResult.certificate.course_title || '—'}</p>
                    </div>
                  </div>

                  {/* Code de vérification */}
                  <div className="bg-white rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Shield className="w-4 h-4 text-gray-600" />
                      <span className="text-gray-600 font-medium">Code de vérification</span>
                    </div>
                    <div className="flex items-center justify-center gap-3">
                      <p className="font-mono text-lg text-gray-900 bg-gray-50 px-4 py-2 rounded border">
                        {verificationResult.certificate.verification_code}
                      </p>
                      <button
                        onClick={copyVerificationCode}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                        title="Copier le code"
                      >
                        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => navigate(`/verify?code=${encodeURIComponent(verificationResult.certificate.verification_code)}`)}
                    className="flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors duration-200"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Voir le certificat complet
                  </button>
                  <button
                    onClick={() => window.location.href = '/courses'}
                    className="flex items-center gap-2 border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    <BookOpen className="w-4 h-4" />
                    Découvrir nos cours
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {/* Échec */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <XCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-red-600">Certificat Invalide</h2>
                    <p className="text-gray-600">{verificationResult.error}</p>
                  </div>
                </div>

                <div className="bg-red-50 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <h3 className="font-semibold text-red-800">Raisons possibles :</h3>
                  </div>
                  <ul className="list-disc list-inside text-red-700 space-y-2">
                    <li>Le code de vérification est incorrect</li>
                    <li>Le certificat a été révoqué</li>
                    <li>Le certificat a expiré</li>
                    <li>Le certificat n'existe pas dans notre base de données</li>
                  </ul>
                </div>

                <div className="mt-6 text-center">
                  <button
                    onClick={() => setVerificationResult(null)}
                    className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200"
                  >
                    Essayer un autre code
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Informations sur la vérification */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">
            Comment fonctionne la vérification ?
          </h3>
          <div className="space-y-2 text-blue-800">
            <p>• Chaque certificat possède un code de vérification unique</p>
            <p>• Ce code permet de vérifier l'authenticité du certificat</p>
            <p>• Les certificats vérifiés sont stockés de manière sécurisée</p>
            <p>• Vous pouvez partager ce code pour prouver l'authenticité</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyCertificate;
